import React from 'react';
import PropTypes from 'prop-types';
import { Typography, useThemeTokens } from '@telus-uds/components-base';
import styled from 'styled-components';
import { getAriaLabel, getTimestamp } from '../helpers';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ButtonContainer = /*#__PURE__*/styled.button.withConfig({
  displayName: "SplashButtonWithDetails__ButtonContainer",
  componentId: "components-web__sc-1edwy9o-0"
})({
  background: 'none',
  border: 0,
  position: 'absolute',
  width: '100%',
  height: '100%',
  padding: 0,
  cursor: 'pointer'
});
const ButtonContent = /*#__PURE__*/styled.div.withConfig({
  displayName: "SplashButtonWithDetails__ButtonContent",
  componentId: "components-web__sc-1edwy9o-1"
})({
  background: _ref => {
    let {
      buttonBackground
    } = _ref;
    return buttonBackground;
  },
  border: _ref2 => {
    let {
      buttonBorderColor
    } = _ref2;
    return `1px solid ${buttonBorderColor}`;
  },
  borderRadius: _ref3 => {
    let {
      buttonRadius
    } = _ref3;
    return `${buttonRadius}px`;
  },
  position: 'absolute',
  left: _ref4 => {
    let {
      buttonLeftPosition
    } = _ref4;
    return `${buttonLeftPosition}px`;
  },
  bottom: _ref5 => {
    let {
      buttonBottomPosition
    } = _ref5;
    return `${buttonBottomPosition}px`;
  },
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  minHeight: _ref6 => {
    let {
      buttonMinHeight
    } = _ref6;
    return `${buttonMinHeight}px`;
  },
  paddingRight: _ref7 => {
    let {
      buttonPaddingLeft
    } = _ref7;
    return `${buttonPaddingLeft}px`;
  },
  paddingLeft: _ref8 => {
    let {
      buttonPaddingRight
    } = _ref8;
    return `${buttonPaddingRight}px`;
  },
  transition: 'background 0.2s ease-in-out',
  [`${ButtonContainer}:hover &`]: {
    background: _ref9 => {
      let {
        buttonContentChildrenBackground
      } = _ref9;
      return buttonContentChildrenBackground;
    }
  }
});
const PlayIconContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "SplashButtonWithDetails__PlayIconContainer",
  componentId: "components-web__sc-1edwy9o-2"
})({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 32,
  height: 32,
  background: _ref10 => {
    let {
      playIconContainerBackground
    } = _ref10;
    return playIconContainerBackground ?? 'none';
  },
  borderRadius: '100%',
  transition: 'background 0.2s ease-in-out'
});
const DetailsContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "SplashButtonWithDetails__DetailsContainer",
  componentId: "components-web__sc-1edwy9o-3"
})({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  paddingLeft: _ref11 => {
    let {
      detailsContainerPadding
    } = _ref11;
    return `${detailsContainerPadding}px`;
  }
});
const selectPlayIconContainerTokens = _ref12 => {
  let {
    playIconContainerBackground
  } = _ref12;
  return {
    playIconContainerBackground
  };
};
const selectDetailsContainerTokens = _ref13 => {
  let {
    detailsContainerPadding
  } = _ref13;
  return {
    detailsContainerPadding
  };
};
const selectButtonContentTokens = _ref14 => {
  let {
    buttonBackground,
    buttonBorderColor,
    buttonRadius,
    buttonLeftPosition,
    buttonBottomPosition,
    buttonMinHeight,
    buttonPaddingLeft,
    buttonPaddingRight
  } = _ref14;
  return {
    buttonBackground,
    buttonBorderColor,
    buttonRadius,
    buttonLeftPosition,
    buttonBottomPosition,
    buttonMinHeight,
    buttonPaddingLeft,
    buttonPaddingRight
  };
};
const getLabelTokens = _ref15 => {
  let {
    labelFontName: fontName,
    labelFontWeight: fontWeight
  } = _ref15;
  return {
    fontName,
    fontWeight
  };
};
const SplashButtonWithDetails = _ref16 => {
  let {
    label,
    tokens,
    variant,
    copy,
    videoLength
  } = _ref16;
  const {
    buttonContentChildrenBackground
  } = useThemeTokens('SplashButtonWithDetails', tokens, variant, {
    hover: true
  });
  const {
    playIcon: PlayIcon,
    playIconColor,
    ...themeTokens
  } = useThemeTokens('SplashButtonWithDetails', tokens, variant);
  const mappedVideoLength = getTimestamp(videoLength, copy);
  const ariaLabel = getAriaLabel(mappedVideoLength, copy);
  return /*#__PURE__*/_jsx(ButtonContainer, {
    "aria-label": ariaLabel,
    children: /*#__PURE__*/_jsxs(ButtonContent, {
      ...selectButtonContentTokens(themeTokens),
      buttonContentChildrenBackground: buttonContentChildrenBackground,
      children: [/*#__PURE__*/_jsx(PlayIconContainer, {
        ...selectPlayIconContainerTokens(themeTokens),
        children: /*#__PURE__*/_jsx(PlayIcon, {
          size: 13,
          color: playIconColor
        })
      }), /*#__PURE__*/_jsxs(DetailsContainer, {
        ...selectDetailsContainerTokens(themeTokens),
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: {
            bold: true
          },
          tokens: getLabelTokens(themeTokens),
          children: label
        }), /*#__PURE__*/_jsx(Typography, {
          variant: {
            colour: 'secondary' /* TODO: this is not the same color as in designs */,
            size: 'micro'
          },
          children: mappedVideoLength.timestamp
        })]
      })]
    })
  });
};
SplashButtonWithDetails.propTypes = {
  label: PropTypes.string,
  tokens: PropTypes.object,
  variant: PropTypes.object,
  videoLength: PropTypes.number,
  copy: PropTypes.oneOf(['en', 'fr'])
};
export default SplashButtonWithDetails;